/**
 * Appearance toggle — System / Light / Dark. Lives in the account area of the
 * nav. Applies the choice instantly (ThemeProvider) and persists it to the
 * user's profile (PATCH /me/theme), rolling back the local change on failure.
 */
import { Monitor, Moon, Sun } from 'lucide-react';
import { toast } from 'sonner';
import type { ThemePreference } from '@vari-crew/schemas';
import { useUpdateThemePreference } from '@/api/theme';
import { useTheme } from '@/theme/ThemeProvider';
import { cn } from '@/lib/cn';

const OPTIONS: { value: ThemePreference; label: string; Icon: typeof Monitor }[] = [
  { value: 'system', label: 'System', Icon: Monitor },
  { value: 'light', label: 'Light', Icon: Sun },
  { value: 'dark', label: 'Dark', Icon: Moon },
];

export function ThemeToggle() {
  const { preference, setPreference } = useTheme();
  const update = useUpdateThemePreference();

  function choose(next: ThemePreference) {
    if (next === preference) return;
    const previous = preference;
    setPreference(next); // instant: local state + localStorage
    update.mutate(next, {
      onError: () => {
        setPreference(previous); // undo the optimistic local change
        toast.error('Couldn’t save your theme — please try again');
      },
    });
  }

  return (
    <div
      role="group"
      aria-label="Appearance"
      className="grid grid-cols-3 gap-1 rounded-md border border-line p-0.5"
    >
      {OPTIONS.map(({ value, label, Icon }) => {
        const active = preference === value;
        return (
          <button
            key={value}
            type="button"
            onClick={() => choose(value)}
            aria-pressed={active}
            title={label}
            className={cn(
              'flex items-center justify-center h-7 rounded transition-colors',
              active ? 'bg-accent text-accent-fg' : 'text-ink-faint hover:text-ink hover:bg-fill-soft',
            )}
          >
            <Icon className="w-3.5 h-3.5" />
            <span className="sr-only">{label}</span>
          </button>
        );
      })}
    </div>
  );
}
